import React, { useEffect, useState } from 'react';
import { studyReportAPI } from '../../services/api';
import './StudyReport.css';

interface SubjectVerdict {
  subject: string;
  status: 'on_track' | 'needs_work' | 'just_started';
  summary: string;
  top_gap: string | null;
}

interface TopPriority {
  subject: string;
  topic: string;
  reason: string;
  action: string;
}

interface StudyReportData {
  readiness_headline: string;
  subject_verdicts: SubjectVerdict[];
  top_priority: TopPriority | null;
  study_tip: string;
  touched_subjects: string[];
  untouched_subjects: string[];
  source: string;
}

interface StudyReportProps {
  isOpen: boolean;
  onClose: () => void;
  onNavigate?: (subject: string, topic?: string) => void;
}

const STATUS_CONFIG = {
  on_track: { label: 'On Track', className: 'status-on-track' },
  needs_work: { label: 'Needs Work', className: 'status-needs-work' },
  just_started: { label: 'Just Started', className: 'status-just-started' },
};

const StudyReport: React.FC<StudyReportProps> = ({ isOpen, onClose, onNavigate }) => {
  const [report, setReport] = useState<StudyReportData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    let cancelled = false;
    const fetchReport = async () => {
      setLoading(true);
      setError(false);
      try {
        const data = await studyReportAPI.getReport();
        if (!cancelled) setReport(data);
      } catch {
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchReport();
    return () => { cancelled = true; };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="sr-overlay" onClick={onClose}>
      <div className="sr-sheet" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="sr-header">
          <h2 className="sr-title">Study Report</h2>
          <button className="sr-close" onClick={onClose} aria-label="Close">
            &times;
          </button>
        </div>

        {/* Content */}
        <div className="sr-body">
          {loading && (
            <div className="sr-loading">
              <div className="sr-shimmer" />
              <div className="sr-shimmer sr-shimmer-short" />
              <div className="sr-shimmer" />
              <p className="sr-loading-text">Analyzing your progress...</p>
            </div>
          )}

          {error && (
            <div className="sr-error">
              <p>Could not load your study report. Try again later.</p>
              <button className="sr-retry-btn" onClick={() => setError(false)}>
                Retry
              </button>
            </div>
          )}

          {report && !loading && (
            <>
              {/* Headline */}
              {report.readiness_headline && (
                <div className="sr-headline">
                  <p>{report.readiness_headline}</p>
                </div>
              )}

              {/* Subject Verdicts */}
              {(report.subject_verdicts?.length ?? 0) > 0 && (
                <div className="sr-section">
                  <h3 className="sr-section-title">Your Subjects</h3>
                  <div className="sr-verdicts">
                    {report.subject_verdicts.map((v) => {
                      const config = STATUS_CONFIG[v.status] || STATUS_CONFIG.just_started;
                      return (
                        <div key={v.subject} className="sr-verdict-card">
                          <div className="sr-verdict-header">
                            <span className="sr-subject-name">{v.subject}</span>
                            <span className={`sr-status-chip ${config.className}`}>
                              {config.label}
                            </span>
                          </div>
                          <p className="sr-verdict-summary">{v.summary}</p>
                          {v.top_gap && (
                            <p className="sr-verdict-gap">Gap: {v.top_gap}</p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Top Priority */}
              {report.top_priority && (
                <div className="sr-section">
                  <h3 className="sr-section-title">Top Priority</h3>
                  <div className="sr-priority-card">
                    <div className="sr-priority-subject">{report.top_priority.subject}</div>
                    <div className="sr-priority-topic">{report.top_priority.topic}</div>
                    <p className="sr-priority-reason">{report.top_priority.reason}</p>
                    <button
                      className="sr-priority-btn"
                      onClick={() => {
                        onNavigate?.(report.top_priority!.subject, report.top_priority!.topic);
                        onClose();
                      }}
                    >
                      {report.top_priority.action} →
                    </button>
                  </div>
                </div>
              )}

              {/* Study Tip */}
              {report.study_tip && (
                <div className="sr-tip">
                  <p>{report.study_tip}</p>
                </div>
              )}

              {/* Touched subjects */}
              {report.touched_subjects?.length > 0 && (
                <div className="sr-footer-note">
                  <p>Keep going with <strong>{report.touched_subjects.join(', ')}</strong> to unlock insights</p>
                </div>
              )}

              {/* Untouched subjects */}
              {report.untouched_subjects?.length > 0 && (
                <div className="sr-footer-note sr-footer-neutral">
                  <p>Also available: {report.untouched_subjects.join(', ')}</p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default StudyReport;
